import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Row, Col, Button, Space, Typography, Input, Card, Divider, Form, Select, message, Tag, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faEdit,
  faTrash,
  faTimes,
  faSave,
  faXRay,
  faCamera,
  faNotesMedical,
  faImage,
  faStethoscope,
  faCalendar,
  faUser,
  faHdd
} from '@fortawesome/free-solid-svg-icons'

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input
const { Option } = Select

const categoryIcons = {
  'x-ray': faXRay,
  photo: faCamera,
  scan: faNotesMedical,
  ultrasound: faNotesMedical,
  lab: faImage,
  general: faImage,
  clinical: faStethoscope
}

const categoryColors = {
  'x-ray': '#1890ff',
  photo: '#52c41a',
  scan: '#fa541c',
  ultrasound: '#722ed1',
  lab: '#13c2c2',
  general: '#595959',
  clinical: '#eb2f96'
}

const ImageViewer = ({ image, onUpdate, onDelete, onClose }) => {
  const [editing, setEditing] = useState(false)
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  const imageCategories = [
    { value: 'x-ray', label: 'X-Ray' },
    { value: 'photo', label: 'Photograph' },
    { value: 'scan', label: 'CT/MRI Scan' },
    { value: 'ultrasound', label: 'Ultrasound' },
    { value: 'lab', label: 'Lab Result' },
    { value: 'general', label: 'General' },
    { value: 'clinical', label: 'Clinical' }
  ]

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (date) => {
    if (!date) return 'Unknown'
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleEdit = () => {
    form.setFieldsValue({
      imageName: image.imageName,
      imageNote: image.imageNote || '',
      imageCategory: image.imageCategory || 'general'
    })
    setEditing(true)
  }

  const handleSave = async (values) => {
    setLoading(true)
    try {
      await onUpdate(values)
      setEditing(false)
      message.success('Image updated successfully')
    } catch (error) {
      message.error('Failed to update image')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setEditing(false)
    form.resetFields()
  }

  const getCategoryIcon = (category) => {
    return categoryIcons[category] || faImage
  }

  const getCategoryColor = (category) => {
    return categoryColors[category] || '#595959'
  }

  return (
    <div style={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div
        style={{
          padding: '16px 24px',
          borderBottom: '1px solid #f0f0f0',
          backgroundColor: '#fafafa'
        }}
      >
        <Row justify='space-between' align='middle'>
          <Col>
            <Space>
              <FontAwesomeIcon
                icon={getCategoryIcon(image.imageCategory)}
                style={{ color: getCategoryColor(image.imageCategory) }}
              />
              <Title level={4} style={{ margin: 0 }}>
                {image.imageName || 'Untitled Image'}
              </Title>
              <Tag color={getCategoryColor(image.imageCategory)} style={{ marginLeft: '8px' }}>
                {imageCategories.find((cat) => cat.value === image.imageCategory)?.label || 'General'}
              </Tag>
            </Space>
          </Col>
          <Col>
            <Space>
              {!editing && (
                <Button type='primary' ghost icon={<FontAwesomeIcon icon={faEdit} />} onClick={handleEdit}>
                  Edit
                </Button>
              )}
              <Button danger ghost icon={<FontAwesomeIcon icon={faTrash} />} onClick={onDelete}>
                Delete
              </Button>
              <Button icon={<FontAwesomeIcon icon={faTimes} />} onClick={onClose}>
                Close
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: '24px', overflow: 'auto' }}>
        <Row gutter={24} style={{ height: '100%' }}>
          {/* Left side - Image */}
          <Col xs={24} lg={16} style={{ height: '100%' }}>
            <Card
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#000'
              }}
              styles={{
                body: {
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '12px'
                }
              }}
            >
              <img
                src={image.imageData}
                alt={image.imageName}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  borderRadius: '4px'
                }}
              />
            </Card>
          </Col>

          {/* Right side - Details and editing */}
          <Col xs={24} lg={8}>
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Image Details */}
              <Card title='Image Details' style={{ marginBottom: '16px' }}>
                <Space direction='vertical' style={{ width: '100%' }} size='small'>
                  <div>
                    <Text type='secondary'>
                      <FontAwesomeIcon icon={faCalendar} style={{ marginRight: '8px' }} />
                      Uploaded:
                    </Text>
                    <br />
                    <Text>{formatDate(image.uploadedAt)}</Text>
                  </div>

                  <div>
                    <Text type='secondary'>
                      <FontAwesomeIcon icon={faUser} style={{ marginRight: '8px' }} />
                      Uploaded by:
                    </Text>
                    <br />
                    <Text>{image.uploadedBy || 'Unknown'}</Text>
                  </div>

                  {image.fileSize && (
                    <div>
                      <Text type='secondary'>
                        <FontAwesomeIcon icon={faHdd} style={{ marginRight: '8px' }} />
                        File size:
                      </Text>
                      <br />
                      <Text>{formatFileSize(image.fileSize)}</Text>
                    </div>
                  )}

                  {image.imageType && (
                    <div>
                      <Text type='secondary'>Type:</Text>
                      <br />
                      <Text>{image.imageType}</Text>
                    </div>
                  )}
                </Space>
              </Card>

              {/* Editing Form or Display */}
              <Card
                title='Image Information'
                style={{ flex: 1 }}
                extra={
                  editing && (
                    <Space>
                      <Button size='small' onClick={handleCancel} icon={<FontAwesomeIcon icon={faTimes} />}>
                        Cancel
                      </Button>
                      <Button
                        type='primary'
                        size='small'
                        loading={loading}
                        onClick={() => form.submit()}
                        icon={<FontAwesomeIcon icon={faSave} />}
                      >
                        Save
                      </Button>
                    </Space>
                  )
                }
              >
                {editing ? (
                  <Form form={form} layout='vertical' onFinish={handleSave} size='small'>
                    <Form.Item
                      name='imageName'
                      label='Name'
                      rules={[{ required: true, message: 'Please enter a name' }]}
                    >
                      <Input placeholder='Enter image name' />
                    </Form.Item>

                    <Form.Item
                      name='imageCategory'
                      label='Category'
                      rules={[{ required: true, message: 'Please select a category' }]}
                    >
                      <Select placeholder='Select category'>
                        {imageCategories.map((category) => (
                          <Option key={category.value} value={category.value}>
                            <Space>
                              <FontAwesomeIcon icon={getCategoryIcon(category.value)} />
                              {category.label}
                            </Space>
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item name='imageNote' label='Notes'>
                      <TextArea rows={8} placeholder='Add notes about this image...' maxLength={1000} showCount />
                    </Form.Item>
                  </Form>
                ) : (
                  <div>
                    <div style={{ marginBottom: '16px' }}>
                      <Text strong>Name:</Text>
                      <br />
                      <Text>{image.imageName || 'Untitled'}</Text>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <Text strong>Category:</Text>
                      <br />
                      <Space>
                        <FontAwesomeIcon icon={getCategoryIcon(image.imageCategory)} />
                        <Text>
                          {imageCategories.find((cat) => cat.value === image.imageCategory)?.label || 'General'}
                        </Text>
                      </Space>
                    </div>

                    <div>
                      <Text strong>Notes:</Text>
                      <br />
                      {image.imageNote ? (
                        <Paragraph style={{ marginTop: '8px', whiteSpace: 'pre-wrap' }}>{image.imageNote}</Paragraph>
                      ) : (
                        <Text type='secondary' style={{ fontStyle: 'italic' }}>
                          No notes added
                        </Text>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  )
}

ImageViewer.propTypes = {
  image: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    imageName: PropTypes.string,
    imageNote: PropTypes.string,
    imageData: PropTypes.string.isRequired,
    imageCategory: PropTypes.string,
    uploadedAt: PropTypes.string,
    uploadedBy: PropTypes.string,
    fileSize: PropTypes.number,
    imageType: PropTypes.string
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
}

export default ImageViewer
